import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { ContainerWithSideDetailViewModule } from '@y42/core/container-with-side-detail-view';
import { DatasourceTableQuery } from '@y42/datasource';
import { TableViewerModule } from '@y42/datasource/shared/table-viewer';
import { FeatureAggregationService } from '@y42/feature';
import { agGridJobIcon, dataTestStrokedIcon } from '@y42/feature/icons';
import { isNullish } from '@y42/util/nullish';
import { takePromise } from '@y42/util/take-promise';
import { AngularSplitModule } from 'angular-split';
import { BehaviorSubject, map } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DataTestBuilderContainerModule } from '../../data-test-builder-container/data-test-builder-container.module';
import { DataTestJobOverviewListModule } from '../data-test-job-overview/data-test-job-overview-list.module';
import { DataTestSetupListModule } from '../data-test-setup-list/data-test-setup-list.module';
import { DataTestDialogStateService } from './data-test-dialog-state.service';

export interface DataTestDialogData {
  path: string;
}

@UntilDestroy()
@Component({
  selector: 'y42-data-test-dialog',
  templateUrl: './data-test-dialog.component.html',
  styleUrls: ['./data-test-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataTestDialogStateService],
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatButtonModule,
    MatDialogModule,

    FontAwesomeModule,
    AngularSplitModule,

    DataTestJobOverviewListModule,
    TableViewerModule,
    ContainerWithSideDetailViewModule,
    DataTestSetupListModule,
    DataTestBuilderContainerModule,
  ],
})
export class DataTestDialogComponent implements OnInit {
  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly params: DataTestDialogData,
    private readonly datasourceTableQuery: DatasourceTableQuery,
    private readonly dataTestDialogStateService: DataTestDialogStateService,
    private readonly featureAggregationService: FeatureAggregationService,
  ) {}

  readonly jobOverviewIcon = agGridJobIcon;
  readonly testSetupIcon = dataTestStrokedIcon;

  private readonly dataPreviewOpened$$ = new BehaviorSubject(false);
  private readonly tabSelectedIndex$$ = new BehaviorSubject<number>(0);
  private readonly dataTestsCount$ = this.dataTestDialogStateService.path$.pipe(
    switchMap((path) => this.datasourceTableQuery.selectUIEntity(path)),
    map((uiEntity) => uiEntity?.tests?.tests.length ?? 0),
  );

  readonly state = connectState(this, {
    tablePath: this.dataTestDialogStateService.path$,
    dataPreviewOpened: this.dataPreviewOpened$$,
    dataPreviewButtonDisabled: this.datasourceTableQuery
      .selectUIEntity(this.params.path)
      .pipe(map((res) => isNullish(res?.latest_valid_job))),
    tabSelectedIndex: this.tabSelectedIndex$$,
    headerIcon: this.dataTestDialogStateService.path$.pipe(
      map((path) => (path === '' ? null : this.featureAggregationService.getLogoByPath(path))),
    ),
  });

  toggleDataPreview() {
    this.dataPreviewOpened$$.next(!this.dataPreviewOpened$$.value);
  }

  changeTabSelectedIndex(index: number): void {
    this.tabSelectedIndex$$.next(index);
  }

  ngOnInit(): void {
    this.dataTestDialogStateService.setState({ $path: this.params.path });
    this.setDefaultTabOnDialogOpen();
  }

  private async setDefaultTabOnDialogOpen(): Promise<void> {
    const testsCount = await takePromise(this.dataTestsCount$);
    const tabIndex = testsCount === 0 ? 1 : 0;
    this.tabSelectedIndex$$.next(tabIndex);
  }
}
